import type {BrandTagFragment} from '../../brand/query';
import {
	getAllEmbroideryOptionBrands,
	getConfiguredEmbroidery,
} from '../../embroidery-collection/configuration';
import type {ProductFilters} from '../../filters/query';
import {intersectMatchesAndFilters} from '../../filters/utils';

import type {CabTableConfiguration} from './configuration';
import {getDefaultCabTableConfiguration} from './configuration';
import type {CabTableProductCoreFragment} from './query';

type VehicleCompatibility = NonNullable<
	CabTableProductCoreFragment['vehicleCompatibility']
>[number];

export function getBaseCabTableProductBrandsById(product: CabTableProductCoreFragment) {
	const brandsById = new Map<string, BrandTagFragment>();

	// Start with the product's own brands:
	for (const productBrand of product.brands || []) {
		if (!brandsById.has(productBrand._id)) {
			brandsById.set(productBrand._id, productBrand);
		}
	}

	// Add brands from vehicle brand:
	for (const vehicleModel of product.vehicleCompatibility || []) {
		const brand = vehicleModel.resolvedBrand;
		if (brand && !brandsById.has(brand._id)) {
			brandsById.set(brand._id, brand);
		}
	}

	return brandsById;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getAllCabTableProductBrandsById(product: CabTableProductCoreFragment) {
	const brandsById = getBaseCabTableProductBrandsById(product);

	// Only embroideries are branded options for cab table products:
	if (product.settings?.embroidery) {
		const embroideryBrands = getAllEmbroideryOptionBrands(product.settings.embroidery);
		for (const brand of embroideryBrands) {
			if (!brandsById.has(brand._id)) {
				brandsById.set(brand._id, brand);
			}
		}
	}
	return brandsById;
}

export function getConfiguredCabTableProductBrands(
	product: CabTableProductCoreFragment,
	configuration: CabTableConfiguration,
) {
	const brandsById = getBaseCabTableProductBrandsById(product);

	// Only embroideries are branded options for cab table products:
	if (product.settings?.embroidery && configuration.embroidery) {
		const {configuredEmbroidery} = getConfiguredEmbroidery(
			product.settings.embroidery,
			configuration.embroidery,
		);

		const embroideryBrands = configuredEmbroidery?.brands;
		if (embroideryBrands) {
			for (const brand of embroideryBrands) {
				if (!brandsById.has(brand._id)) {
					brandsById.set(brand._id, brand);
				}
			}
		}
	}

	return Array.from(brandsById.values());
}

function getAllCabTableVehicleCompatibility(product: CabTableProductCoreFragment) {
	const modelsById = new Map<string, VehicleCompatibility>();
	const brandsById = new Map<string, NonNullable<VehicleCompatibility['resolvedVehicleBrand']>>();
	const typesById = new Map<string, NonNullable<VehicleCompatibility['resolvedVehicleType']>>();

	if (product.vehicleCompatibility?.length) {
		for (const vehicleModel of product.vehicleCompatibility) {
			const vehicleBrand = vehicleModel.resolvedVehicleBrand;
			const vehicleType = vehicleModel.resolvedVehicleType;

			if (!modelsById.has(vehicleModel._id)) {
				modelsById.set(vehicleModel._id, vehicleModel);
			}

			if (vehicleBrand) {
				if (!brandsById.has(vehicleBrand._id)) {
					brandsById.set(vehicleBrand._id, vehicleBrand);
				}
			}

			if (vehicleType) {
				if (!typesById.has(vehicleType._id)) {
					typesById.set(vehicleType._id, vehicleType);
				}
			}
		}
	}

	return {
		modelsById,
		brandsById,
		typesById,
	};
}

export function getFiltersMatchingCabTableProduct(
	product: CabTableProductCoreFragment,
	filters: ProductFilters,
): ProductFilters | null {
	const vehicleCompatibility = getAllCabTableVehicleCompatibility(product);

	const matches = {
		// We only match on the base brands here to since we genereally only to show interiors that
		// fit a specific vehicle brand. We don't want to show a Massey Ferguson table that can
		// be embroidered with a John Deere logo when filtering on John Deere, for instance.
		brandIds: Array.from(getBaseCabTableProductBrandsById(product).keys()),
		vehicleModelIds: Array.from(vehicleCompatibility.modelsById.keys()),
		vehicleBrandIds: Array.from(vehicleCompatibility.brandsById.keys()),
		vehicleTypesIds: Array.from(vehicleCompatibility.typesById.keys()),
	} satisfies ProductFilters;

	return intersectMatchesAndFilters(matches, filters);
}

export function getCabTableConfigurationAppropriateForFilters(
	product: CabTableProductCoreFragment,
	_filters?: ProductFilters,
): CabTableConfiguration {
	// FIXME: Implement!
	return getDefaultCabTableConfiguration(product);
}
